import { Box, Button, FormHelperText, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import { useRouter } from "next/router";
import { FC, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikCodeField } from "components/form/FormikCodeField";
import { FormikTextField } from "components/form/FormikTextField";

import { ROUTES } from "constants/routes";
import useAuth from "hooks/useAuth";
import useIsMountedRef from "hooks/useIsMountedRef";
import { fetchCurrentSession } from "requests/auth";
import { updateUser } from "requests/user";

import { ApiError } from "types/api";

export interface LoginFormState {
  email: string;
  password: string;
  submit?: string;
  code: string[];
}

const initialValues: LoginFormState = {
  email: "",
  password: "",
  code: ["", "", "", "", "", ""],
};

interface LoginFormProps {
  setVerifying: React.Dispatch<React.SetStateAction<boolean>>;
  setLoginValues: React.Dispatch<
    React.SetStateAction<{
      email: string;
      password: string;
    }>
  >;
  setChangePassword: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LoginForm: FC<LoginFormProps> = ({
  setVerifying,
  setLoginValues,
  setChangePassword,
}) => {
  const isMountedRef = useIsMountedRef();
  const router = useRouter();
  const { resendConfirmationEmail, login, confirmMFACode } = useAuth();
  const { t } = useTranslation();
  const [requireMfa, setRequireMfa] = useState(false);

  const loginFormMfaSchema: Yup.SchemaOf<LoginFormState> = Yup.object({
    email: Yup.string().required(t("login.email.error")),
    password: Yup.string().required(t("login.password.error")),
    submit: Yup.string(),
    code: Yup.array().of(Yup.string().required()).required(),
  });

  // @ts-expect-error No good way to get yup to work with empty array
  const loginFormSchema: Yup.SchemaOf<LoginFormState> = Yup.object({
    email: Yup.string().required(t("login.email.error")),
    password: Yup.string().required(t("login.password.error")),
    submit: Yup.string(),
    code: Yup.array().of(Yup.string()),
  });

  const handleSubmitLogin = async (
    values: LoginFormState,
    { setErrors, setSubmitting }: FormikHelpers<LoginFormState>
  ) => {
    try {
      let status = null;
      if (!requireMfa) {
        status = await login({
          email: values.email,
          password: values.password,
        });
      } else {
        status = await confirmMFACode({
          email: values.email,
          mfaCode: values.code.join(""),
        });
      }

      if (isMountedRef.current) {
        setSubmitting(false);
      }
      switch (status) {
        case "FORCE_CHANGE_PASSWORD":
          setLoginValues({
            email: values.email,
            password: values.password,
          });
          setChangePassword(true);
          break;
        case "NEED_MFA":
          setRequireMfa(true);

          setErrors({ submit: "MFA Code required" });
          break;
        case "CONFIRMED": {
          const currentSession = await fetchCurrentSession();

          await updateUser(
            currentSession.user.organization_id ?? "",
            currentSession.user.user_id,
            { confirmed: true, last_login: new Date() }
          );

          router.push("/");
          break;
        }
        case "NOT_CONFIRMED":
          await resendConfirmationEmail({ email: values.email });
          setLoginValues({
            email: values.email,
            password: values.password,
          });
          setVerifying(true);
          break;
        default:
          router.push(ROUTES.AUTH_LOGIN);
      }
    } catch (err: unknown) {
      if (isMountedRef.current) {
        const error = err as AxiosError<ApiError>;
        setErrors({ submit: error.response?.data.message ?? error.message });
        setSubmitting(false);
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={requireMfa ? loginFormMfaSchema : loginFormSchema}
      onSubmit={handleSubmitLogin}
    >
      {({ errors, isSubmitting }) => (
        <Form>
          <FormikTextField
            name="email"
            autoFocus
            autoComplete="email"
            fullWidth
            label={t("login.email.useremail")}
            margin="normal"
            type="email"
          />
          <FormikTextField
            name="password"
            autoComplete="password"
            fullWidth
            label={t("login.password.label")}
            margin="normal"
            type="password"
          />
          {requireMfa && (
            <>
              <Typography color="textSecondary" variant="subtitle2">
                {t("verify_mfa.verification_code")}
              </Typography>
              <FormikCodeField codeLength={6} name="code" />
            </>
          )}

          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Button
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              color="secondary"
            >
              <Typography sx={{ fontWeight: "900", color: "#333333" }}>
                {t("login.loginbutton")}
              </Typography>
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
